import { useLocation, Navigate, Link } from 'react-router-dom';
import { Phone, ArrowRight, Check, MapPin } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { LOCATIONS, SERVICES, BUSINESS, FAQS } from '../data/content';
import QuoteForm from '../components/QuoteForm';
import TrustBar from '../components/TrustBar';
import SEO from '../components/SEO';
import Home from './Home';

const PATTERNS = [
  { prefix: 'man-and-van-', servicePrefix: 'man-and-van', serviceLabel: 'Man and Van' },
  { prefix: 'office-removals-', servicePrefix: 'office-removals', serviceLabel: 'Office Removals' },
  { prefix: 'packing-services-', servicePrefix: 'packing-services', serviceLabel: 'Packing Services' },
  { prefix: 'furniture-delivery-', servicePrefix: 'furniture-delivery', serviceLabel: 'Furniture Delivery' },
  { prefix: 'removals-', servicePrefix: 'removals', serviceLabel: 'Removals' },
];

const VALID_SLUGS = new Set(LOCATIONS.map(l => l.slug));

// Dispatches dash-prefixed location URLs (e.g. /removals-greenwich, /man-and-van-greenwich)
// to LocationPage with the correct service props. Falls back to Home for unknown URLs.
export default function LocationDispatcher() {
  const { pathname } = useLocation();
  const path = pathname.startsWith('/') ? pathname.slice(1) : pathname;

  for (const p of PATTERNS) {
    if (path.startsWith(p.prefix)) {
      const slug = path.slice(p.prefix.length).replace(/\/$/, '');
      if (VALID_SLUGS.has(slug)) {
        return <LocationView key={path} slug={slug} servicePrefix={p.servicePrefix} serviceLabel={p.serviceLabel} />;
      }
    }
  }
  return <Home />;
}

function LocationView({ slug, servicePrefix, serviceLabel }) {
  const location = LOCATIONS.find(l => l.slug === slug);
  if (!location) return <Navigate to="/service-areas" replace />;
  const titleLong = `${serviceLabel} in ${location.name}, ${location.region} | Laraé Logistics`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `Laraé Logistics — ${serviceLabel} ${location.name}`,
    telephone: BUSINESS.phone,
    address: { '@type': 'PostalAddress', addressLocality: location.name, addressRegion: 'London', postalCode: location.region, addressCountry: 'GB' },
    priceRange: '££',
    areaServed: location.name,
  };

  return (
    <>
      <SEO title={titleLong}
        description={`Professional ${serviceLabel.toLowerCase()} in ${location.name} (${location.region}). Fully insured, family-run, fixed quotes. Call ${BUSINESS.phone} for a free quote today.`}
        jsonLd={jsonLd} />
      <section className="bg-forest text-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex items-center gap-2 text-oat text-sm font-manrope mb-4"><MapPin size={14} /> {location.name} · {location.region}</div>
          <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-3xl">{serviceLabel} in {location.name}.</h1>
          <p className="font-manrope text-base md:text-lg text-white/80 mt-5 max-w-2xl leading-relaxed">
            Trusted, fully insured {serviceLabel.toLowerCase()} for homes and businesses in {location.name} and surrounding {location.region} postcodes. Local crews, fixed quotes, no surprises.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/quote" className="bg-oat text-ink font-semibold rounded-full px-7 py-4 inline-flex items-center justify-center gap-2">Get Free Quote <ArrowRight size={18} /></Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="bg-white/10 border border-white/30 text-white font-medium rounded-full px-7 py-4 inline-flex items-center justify-center gap-2"><Phone size={18} /> {BUSINESS.phone}</a>
          </div>
        </div>
      </section>
      <TrustBar />
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">Local expertise</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium text-ink tracking-tight">Why we&apos;re the right choice in {location.name}.</h2>
            <p className="font-manrope text-base text-ink/70 mt-5 leading-relaxed">
              We move households and offices in {location.name} every week. Our crews know the area — the parking quirks, the narrow stairwells, the one-way systems — so your move runs smoothly from start to finish.
            </p>
            <ul className="mt-6 space-y-3">
              {[`Local crews who know ${location.name}`, 'Same-day availability often possible', 'Fixed-price, no-surprise quotes', '£1M public liability cover'].map(b => (
                <li key={b} className="flex items-start gap-3"><div className="w-5 h-5 rounded-full bg-forest grid place-items-center shrink-0 mt-0.5"><Check size={12} className="text-white" /></div><span className="font-manrope text-sm text-ink/80">{b}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-outfit text-xl font-semibold text-ink mb-4">All services available in {location.name}</h3>
            <div className="grid grid-cols-2 gap-3">
              {SERVICES.map(s => (
                <Link to={`/services/${s.slug}`} key={s.slug} className="bg-white border border-sand rounded-xl p-4 hover:border-forest transition-all">
                  <div className="font-outfit text-sm font-semibold text-ink">{s.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="bg-sand/50 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center max-w-xl mx-auto mb-8">
            <h2 className="font-outfit text-3xl md:text-4xl font-medium text-ink tracking-tight">Get your {location.name} quote</h2>
            <p className="font-manrope text-base text-ink/65 mt-3">Local crews. Fixed prices. Same-day replies.</p>
          </div>
          <QuoteForm sourcePage={`${servicePrefix}-${slug}`} />
        </div>
      </section>
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          <h2 className="font-outfit text-2xl md:text-3xl font-medium text-ink mb-6 text-center">FAQs about moving in {location.name}</h2>
          <Accordion type="single" collapsible>
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-sand">
                <AccordionTrigger className="font-outfit text-base font-medium text-ink text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="font-manrope text-sm text-ink/70">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
